/**
 * Piezas compartidas del rediseño de Biblioteca (20.H) — port del bundle
 * (biblioteca-shared.jsx): marca de familiaridad, fila unificada, separador
 * de banda (una línea, opaco, NO sticky), sheet base, línea-resumen de
 * cobertura + su sheet con el medidor, y estados (loading/error/sin resultados).
 */
import type { ReactNode } from 'react';
import type { Coverage } from '../../lib/lexiconApi.js';
import type { FamiliarityTier } from '../../components/familiarity/Familiarity.js';
import type { VocabTag } from '../vocabulary/vocabData.js';
import { TagBadge } from '../vocabulary/VocabBits.js';
import { isStarterTier, libCoverage, libRowTr, type LibWord, type LibBand } from './libData.js';
import {
  LibIcBookmark,
  LibIcCheck,
  LibIcCheckC,
  LibIcChevR,
  LibIcClose,
  LibIcLayers,
  LibIcRetry,
  LibIcSearch,
  libSvg,
} from './libIcons.js';
import { useSheetDrag } from '../../hooks/useSheetDrag.js';
import { useLongPress } from '../../hooks/useLongPress.js';

// ─── Familiaridad: mismo eje de 4 estados del app (Familiarity.tsx) ──
interface FamMeta {
  label: string;
  color: string;
  fill: number;
  dashed: boolean;
  glyph: 'dot' | 'bars' | 'check' | 'frost';
}

export const LIB_FAM: Readonly<Record<FamiliarityTier, FamMeta>> = {
  nueva: { label: 'Nueva', color: 'oklch(0.78 0.13 45)', fill: 0.28, dashed: false, glyph: 'dot' },
  practica: { label: 'En práctica', color: 'oklch(0.84 0.16 85)', fill: 0.62, dashed: false, glyph: 'bars' },
  afianzada: { label: 'Afianzada', color: 'oklch(0.91 0.19 117)', fill: 1, dashed: false, glyph: 'check' },
  dormida: { label: 'Dormida', color: 'oklch(0.72 0.04 250)', fill: 0.85, dashed: true, glyph: 'frost' },
};

export function LibFamMark({ fam, size = 21 }: { fam: FamiliarityTier | null; size?: number }) {
  if (!fam) {
    return (
      <span className="bib-mark unseen" title="Sin ver" aria-label="Sin ver">
        <i></i>
      </span>
    );
  }
  const t = LIB_FAM[fam];
  const stroke = Math.max(2, Math.round(size * 0.11));
  const r = (size - stroke) / 2 - 0.5;
  const c = 2 * Math.PI * r;
  return (
    <span className="bib-mark" title={t.label} aria-label={t.label}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="oklch(1 0 0 / 0.1)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={t.color}
          strokeWidth={stroke}
          strokeDasharray={t.dashed ? `${(c * t.fill) / 8} ${(c * (1 - t.fill)) / 8}` : `${c * t.fill} ${c}`}
          strokeLinecap="round"
        />
      </svg>
      <span style={{ position: 'absolute', color: t.color, display: 'flex' }}>
        {t.glyph === 'check' && <LibIcCheck size={size * 0.5} />}
        {t.glyph === 'bars' && libSvg(size * 0.5, <path d="M8 14 V 17 M12 10 V 17 M16 13 V 17" />, 2.6)}
        {t.glyph === 'frost' && libSvg(size * 0.5, <path d="M12 5 V 19 M5.9 8.5 L 18.1 15.5 M18.1 8.5 L 5.9 15.5" />, 2)}
        {t.glyph === 'dot' && (
          <svg viewBox="0 0 24 24" width={size * 0.5} height={size * 0.5} fill={t.color}>
            <circle cx="12" cy="12" r="3.4" />
          </svg>
        )}
      </span>
    </span>
  );
}

/** Estado glanceable del catálogo: sin ver / familiaridad / dominada. */
export function LibCatMark({ w }: { w: LibWord }) {
  if (w.dom) {
    const label = w.said ? 'Dominada — la dijiste' : 'Dominada — la marcaste';
    return (
      <span className="bib-mark" title={label} aria-label={label}>
        <LibIcCheckC size={21} fill="oklch(0.91 0.19 117 / 0.16)" />
      </span>
    );
  }
  if (w.fam) return <LibFamMark fam={w.fam} size={21} />;
  return (
    <span className="bib-mark unseen" title="Sin ver" aria-label="Sin ver">
      <i></i>
    </span>
  );
}

// ─── Fila de palabra (catálogo y Lo mío comparten anatomía) ──────────
export function LibRow({ w, my = false, tags = [], onTap, onToggleSave, selecting = false, selected = false, current = false, onLongPress }: {
  w: LibWord;
  my?: boolean;
  /** Las etiquetas del usuario sobre la fila, ya resueltas y en su orden (Lo
   *  mío). Se ve la primera como chip y el resto como "+N". */
  tags?: readonly VocabTag[];
  onTap: (w: LibWord) => void;
  onToggleSave?: (w: LibWord) => void;
  /** Modo selección (Lo mío): check en lugar de la marca y sin guardar. Qué
   *  hace el tap lo decide el que pasa `onTap` (ahí, alternar la marca). */
  selecting?: boolean;
  selected?: boolean;
  /** Es la palabra de la ficha abierta al lado (tablet acostada y compu). */
  current?: boolean;
  /** Mantener apretada la fila. Sólo Lo mío lo usa, para entrar a seleccionar. */
  onLongPress?: (w: LibWord) => void;
}) {
  const longPress = useLongPress(() => onLongPress?.(w));
  return (
    <div
      className={`bib-row ${w.dom ? 'is-dom' : ''} ${onLongPress ? 'is-pressable' : ''} ${selected ? 'is-selected' : ''} ${current ? 'is-current' : ''}`}
      onClick={() => onTap(w)}
      {...(onLongPress ? longPress : {})}
      {...(selecting ? { role: 'checkbox', 'aria-checked': selected } : {})}
      {...(current ? { 'aria-current': true } : {})}
    >
      {selecting ? (
        <span className={`bib-check ${selected ? 'on' : ''}`} aria-hidden="true">
          {selected && <LibIcCheck size={13} />}
        </span>
      ) : my ? (
        <LibFamMark fam={w.fam} size={21} />
      ) : (
        <LibCatMark w={w} />
      )}
      <div className="bib-row-main">
        <div className="bib-row-l1">
          <span className="bib-row-en">{w.text}</span>
          {w.ipa && <span className="bib-row-ipa">{w.ipa}</span>}
          {w.fav && (
            <span className="bib-row-fav">
              <LibIcStarFill size={11} />
            </span>
          )}
        </div>
        <div className="bib-row-l2">
          {/* En el buscador (v2), la traducción del significado que coincidió */}
          <span className="bib-row-tr">{libRowTr(w) || w.definition || ''}</span>
          {my && tags[0] && <TagBadge tag={tags[0]} mini />}
          {my && tags.length > 1 && <span className="bml-badge more">+{tags.length - 1}</span>}
          {my && w.fromSessions && <span className="bml-badge coach">de tus sesiones</span>}
          {my && w.isPhrase && <span className="bml-badge phrase">frase</span>}
          {!my && w.kind === 'phrasal_verb' && <span className="bib-row-kind">phrasal</span>}
          {!my && w.verbForms?.irregular && w.kind !== 'phrasal_verb' && <span className="bib-row-kind">irreg.</span>}
        </div>
      </div>
      {!w.isPhrase && !selecting && (
        <button
          className={`bib-row-save ${w.saved ? 'on' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSave?.(w);
          }}
          aria-label={w.saved ? 'Quitar de Lo mío' : 'Guardar en Lo mío'}
          title={w.saved ? 'Guardada' : 'Guardar'}
        >
          <LibIcBookmark size={16} fill={w.saved ? 'currentColor' : 'none'} />
        </button>
      )}
    </div>
  );
}

function LibIcStarFill({ size = 11 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="M12 3.5 L 14.6 9 L 20.5 9.8 L 16.2 14 L 17.2 20 L 12 17.1 L 6.8 20 L 7.8 14 L 3.5 9.8 L 9.4 9 Z" />
    </svg>
  );
}

// ─── Separador de banda — UNA línea, opaco, NO sticky (H4) ───────────
// El conteo `known/total` es la COBERTURA del tramo completo, no un conteo de
// la lista. Bajo cualquier filtro (Me faltan, Verbos, CEFR) la lista es un
// subconjunto, así que el conteo confundiría ("302/500" sobre 12 filas) → se
// oculta y el separador queda como divisor puro. Sólo se muestra sobre el
// catálogo COMPLETO, donde sí es un hito de progreso honesto.
export function LibBandSep({ band, known, showCount = true }: { band: LibBand; known: number; showCount?: boolean }) {
  const beyond = band.band === 99999;
  return (
    <div className="bib-band" style={{ '--band-accent': band.accent } as React.CSSProperties}>
      <span className="bib-band-dot" />
      <b>{band.label}</b>
      {showCount && (
        <span className="n">
          {beyond ? (
            <>
              <b>+{known}</b> dominadas
            </>
          ) : (
            <>
              <b>{known}</b>/{(band.range[1] - band.range[0] + 1).toLocaleString('es')}
            </>
          )}
        </span>
      )}
    </div>
  );
}

// ─── Sheet base ───────────────────────────────────────────────────────
/**
 * `variant="panel"` (tablet acostada y compu, card #592): la misma hoja, sin
 * velo, manija ni cruz, para que quien la monta la ponga fija al lado de la
 * lista. Abre y cierra quien la monta; acá no hay gesto que la cierre.
 */
export function LibSheet({ title, onClose, children, footer, variant = 'sheet' }: {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'sheet' | 'panel';
}) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  if (variant === 'panel') {
    return (
      <div className="bib-sheet is-panel">
        {title && <div className="bib-sheet-title">{title}</div>}
        <div className="bib-sheet-scroll">{children}</div>
        {footer}
      </div>
    );
  }
  return (
    <div className="bib-overlay" onClick={onClose}>
      <div className="bib-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="bib-sheet-handle sheet-grab" {...grabProps} />
        {title && <div className="bib-sheet-title">{title}</div>}
        <button className="bib-sheet-x" onClick={onClose} aria-label="Cerrar">
          <LibIcClose size={13} />
        </button>
        <div className="bib-sheet-scroll">{children}</div>
        {footer}
      </div>
    </div>
  );
}

// ─── Línea-resumen de cobertura (siempre visible, tappeable — H4) ────
export function LibCovLine({ coverage, onOpen }: { coverage: Coverage | null; onOpen: () => void }) {
  const cov = libCoverage(coverage);
  if (!cov.frontier) return null;
  return (
    <button className="bib-covline" onClick={onOpen} aria-label="Ver tu mapa del idioma">
      <b>{cov.totalKnown.toLocaleString('es')}</b>
      <span>palabras dominadas</span>
      <span className="dot">·</span>
      <span className="frontier">
        tu frontera: <b>{cov.frontier.label}</b>
      </span>
      <span className="chev">
        <LibIcChevR size={12} />
      </span>
    </button>
  );
}

// ─── Sheet del medidor — el meter por tramos vive acá ────────────────
export function LibCovSheet({ coverage, onClose }: { coverage: Coverage | null; onClose: () => void }) {
  const cov = libCoverage(coverage);
  const remaining = cov.frontier ? cov.frontier.total - cov.frontier.known : 0;
  return (
    <LibSheet title="Tu mapa del idioma" onClose={onClose}>
      <div className="bcov-head">
        <div className="bcov-big">
          <span className="bcov-big-n">{cov.totalKnown.toLocaleString('es')}</span>
          <span className="bcov-big-l">
            palabras
            <br />
            dominadas
          </span>
        </div>
        {cov.frontier && (
          <div className="bcov-frontier">
            <span className="bcov-frontier-tag">tu frontera</span>
            <span className="bcov-frontier-tier">{cov.frontier.label}</span>
            <span className="bcov-frontier-sub">
              faltan <b>{remaining.toLocaleString('es')}</b>
            </span>
          </div>
        )}
      </div>
      <div className="bcov-tiers">
        {cov.tiers.map((t) => (
          <div key={t.band} className={`bcov-tier ${t.frontier ? 'is-frontier' : ''}`}>
            <div className="bcov-tier-top">
              <span className="bcov-tier-label">{t.label}</span>
              {t.frontier && <span className="bcov-tier-flag">acá</span>}
              {t.pct >= 92 && (
                <span style={{ display: 'flex', color: t.accent }}>
                  <LibIcCheckC size={13} />
                </span>
              )}
              <span className="bcov-tier-stat">
                <b>{t.known.toLocaleString('es')}</b> / {t.total.toLocaleString('es')}
              </span>
              <span className="bcov-tier-pct" style={{ color: t.accent }}>
                {t.pct}%
              </span>
            </div>
            <div className="bcov-bar">
              <div className="bcov-bar-fill" style={{ width: `${t.pct}%`, background: t.accent }} />
            </div>
            {t.marked > 0 && (
              <div className="bcov-split">
                dijiste <b>{t.said.toLocaleString('es')}</b> · marcaste <b>{t.marked.toLocaleString('es')}</b>
              </div>
            )}
            {/* «Primeros pasos» es una lista, no un tramo de frecuencia: no tiene % del idioma. */}
            {isStarterTier(t) ? (
              <div className="bcov-corpus">{t.blurbEs}</div>
            ) : (
              <div className="bcov-corpus">
                <LibIcLayers size={11} />
                <span>
                  estas {t.total.toLocaleString('es')} cubren <b>~{t.corpusPct}%</b> del inglés hablado
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="bcov-foot">
        El <b>% del idioma</b> es cuánto cubre cada tramo — dato fijo del idioma, no tu progreso. Una palabra cuenta como
        dominada cuando la <b>decís</b> (en una charla o en Decila) o cuando la marcás como <b>ya sabida</b>.
      </div>
    </LibSheet>
  );
}

// ─── Estados ─────────────────────────────────────────────────────────
export function LibLoading() {
  return (
    <div className="bib-scroll" aria-busy="true">
      <div className="bib-state" style={{ flex: 'none', padding: '18px 0 8px' }}>
        <div className="bib-spinner" />
        <span className="bib-state-sub">Cargando el catálogo…</span>
      </div>
      <div className="bib-skel">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bib-skel-row" style={{ opacity: 1 - i * 0.14 }} />
        ))}
      </div>
    </div>
  );
}

export function LibError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="bib-state">
      <div className="bib-state-ic err">
        <LibIcClose size={20} />
      </div>
      <div className="bib-state-title">No pudimos cargar el catálogo</div>
      <div className="bib-state-sub">Revisá tu conexión y volvé a intentar.</div>
      <button className="bib-state-cta ghost" onClick={onRetry}>
        <LibIcRetry size={14} /> Reintentar
      </button>
    </div>
  );
}

export function LibNoResults({ query, onClear }: { query: string; onClear: () => void }) {
  return (
    <div className="bib-state">
      <div className="bib-state-ic">
        <LibIcSearch size={20} />
      </div>
      <div className="bib-state-title">Nada para “{query}”</div>
      <div className="bib-state-sub">Buscamos en inglés y en español. Probá con otra palabra o menos letras.</div>
      <button className="bib-state-cta ghost" onClick={onClear}>
        Borrar búsqueda
      </button>
    </div>
  );
}
